'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useToast } from '@/shared/providers';
import { ehUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { abrirDocumentoEnvioFisicoAr, type FalhaEnvioFisicoAr, fetchEnviosFisicosAr } from '../store/envioFisicoAr/actions';
import { selectAcompanhamentosAr, selectDocumentosEmVooAr, selectPaginacaoEnvioFisicoAr } from '../store/envioFisicoAr/selectors';
import { chaveDoDocumento, limparEnviosFisicosAr } from '../store/envioFisicoAr/slice';
import type { ArbitralDetailTab } from '../types/arbitralDetailTypes';
import type { EnvioFisicoArDocumentoTipo } from '../types/envioFisicoArTypes';
import { envioFisicoArHabilitado } from '../utils/flagEnvioFisicoAr';
function mensagemDa(payload: unknown, padrao: string): FalhaEnvioFisicoAr['mensagem'] {
  if (typeof payload !== 'object' || payload === null || !('mensagem' in payload)) return padrao;
  return typeof payload.mensagem === 'string' ? payload.mensagem : padrao;
}
const TIPOS_DE_DOCUMENTO: readonly EnvioFisicoArDocumentoTipo[] = ['CARTA_ENVIADA', 'FOTO_CORREIOS', 'AR_EFETIVAMENTE_RECEBIDO'];
interface UseEnvioFisicoArInput {
  processoArbitralId: string;
  abaAtiva: ArbitralDetailTab;
  irParaAba: (aba: ArbitralDetailTab) => void;
}

/**
 * A aba "Envio físico" do processo: a lista de cartas AR, paginada, os documentos de cada
 * uma sob demanda, e se a aba existe.
 *
 * A aba só aparece depois que a primeira leitura responde com alguma carta. O 404/403 que o
 * servidor dá a quem está do lado do requerido conta como "nenhuma carta" — ver o slice.
 *
 * Erro técnico só mantém a aba para quem JÁ está nela (deep-link ou retry): a leitura roda ao
 * abrir QUALQUER processo, e um 503 ou 429 criaria uma aba de erro em processo sem AR.
 */
export function useEnvioFisicoAr(t0) {
  const $ = _c(45);
  const {
    processoArbitralId,
    abaAtiva,
    irParaAba
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = envioFisicoArHabilitado();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const habilitado = t1;
  const dispatch = useAppDispatch();
  const toast = useToast();
  const acompanhamentos = useAppSelector(selectAcompanhamentosAr);
  const paginacao = useAppSelector(selectPaginacaoEnvioFisicoAr);
  const documentosEmVoo = useAppSelector(selectDocumentosEmVooAr);
  const naAba = abaAtiva === "envio-fisico";
  const temCarta = paginacao.total > 0 || paginacao.erro !== null && naAba;
  const mostrarAba = habilitado && (paginacao.carregado ? temCarta : naAba);
  const voltarAoResumo = naAba && (!habilitado || paginacao.carregado && !temCarta);
  let t2;
  let t3;
  if ($[1] !== dispatch || $[2] !== processoArbitralId) {
    t2 = () => {
      if (!habilitado) {
        return;
      }
      dispatch(fetchEnviosFisicosAr({
        processoArbitralId,
        page: 1
      }));
      return () => {
        dispatch(limparEnviosFisicosAr());
      };
    };
    t3 = [dispatch, processoArbitralId, habilitado];
    $[1] = dispatch;
    $[2] = processoArbitralId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== irParaAba || $[6] !== voltarAoResumo) {
    t4 = () => {
      if (voltarAoResumo) {
        irParaAba("resumo");
      }
    };
    t5 = [voltarAoResumo, irParaAba];
    $[5] = irParaAba;
    $[6] = voltarAoResumo;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== dispatch || $[10] !== paginacao.carregando || $[11] !== paginacao.carregandoMais || $[12] !== paginacao.paginaQueFalhou || $[13] !== processoArbitralId) {
    t6 = () => {
      if (paginacao.carregando || paginacao.carregandoMais) {
        return;
      }
      const page = paginacao.paginaQueFalhou ?? 1;
      dispatch(fetchEnviosFisicosAr({
        processoArbitralId,
        page
      }));
    };
    $[9] = dispatch;
    $[10] = paginacao.carregando;
    $[11] = paginacao.carregandoMais;
    $[12] = paginacao.paginaQueFalhou;
    $[13] = processoArbitralId;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const recarregar = t6;
  let t7;
  if ($[15] !== dispatch || $[16] !== paginacao.carregando || $[17] !== paginacao.carregandoMais || $[18] !== paginacao.hasMore || $[19] !== paginacao.pagina || $[20] !== processoArbitralId) {
    t7 = () => {
      if (!paginacao.hasMore || paginacao.carregandoMais || paginacao.carregando) {
        return;
      }
      dispatch(fetchEnviosFisicosAr({
        processoArbitralId,
        page: paginacao.pagina + 1
      }));
    };
    $[15] = dispatch;
    $[16] = paginacao.carregando;
    $[17] = paginacao.carregandoMais;
    $[18] = paginacao.hasMore;
    $[19] = paginacao.pagina;
    $[20] = processoArbitralId;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const carregarMais = t7;
  let t8;
  if ($[22] !== toast) {
    t8 = () => {
      const aba = window.open("", "_blank");
      if (!aba) {
        toast.showError("N\xE3o conseguimos abrir o documento", "Libere os pop-ups deste site e tente de novo.");
        return null;
      }
      aba.opener = null;
      return aba;
    };
    $[22] = toast;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const abrirAbaEmBranco = t8;
  let t9;
  if ($[24] !== toast) {
    t9 = (aba_0, titulo, payload) => {
      aba_0.close();
      toast.showError(titulo, mensagemDa(payload, "Tente novamente em instantes."));
    };
    $[24] = toast;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const falhouAoAbrir = t9;
  let t10;
  if ($[26] !== abrirAbaEmBranco || $[27] !== dispatch || $[28] !== documentosEmVoo || $[29] !== falhouAoAbrir || $[30] !== processoArbitralId) {
    t10 = async (envioFisicoId, tipo) => {
      if (documentosEmVoo[chaveDoDocumento(envioFisicoId, tipo)]) {
        return;
      }
      const aba_1 = abrirAbaEmBranco();
      if (!aba_1) {
        return;
      }
      const resultado = await dispatch(abrirDocumentoEnvioFisicoAr({
        processoArbitralId,
        envioFisicoId,
        tipo
      }));
      if (abrirDocumentoEnvioFisicoAr.fulfilled.match(resultado) && ehUrlNavegavelSegura(resultado.payload.url)) {
        aba_1.location.href = resultado.payload.url;
        return;
      }
      falhouAoAbrir(aba_1, "N\xE3o foi poss\xEDvel abrir o documento", resultado.payload);
    };
    $[26] = abrirAbaEmBranco;
    $[27] = dispatch;
    $[28] = documentosEmVoo;
    $[29] = falhouAoAbrir;
    $[30] = processoArbitralId;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const abrirArquivo = t10;
  let t11;
  if ($[32] !== documentosEmVoo) {
    t11 = envioFisicoId_0 => TIPOS_DE_DOCUMENTO.filter(tipo_0 => documentosEmVoo[chaveDoDocumento(envioFisicoId_0, tipo_0)]);
    $[32] = documentosEmVoo;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const documentosAbrindoDe = t11;
  let t12;
  if ($[34] !== abrirArquivo) {
    t12 = (envioFisicoId_1, tipo_1) => {
      if (tipo_1 === "FOTO_CORREIOS") {
        return;
      }
      abrirArquivo(envioFisicoId_1, tipo_1);
    };
    $[34] = abrirArquivo;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const verDocumento = t12;
  const t13 = voltarAoResumo ? "resumo" as const : abaAtiva;
  let t14;
  if ($[36] !== acompanhamentos || $[37] !== carregarMais || $[38] !== documentosAbrindoDe || $[39] !== mostrarAba || $[40] !== paginacao || $[41] !== recarregar || $[42] !== t13 || $[43] !== verDocumento) {
    t14 = {
      mostrarAba,
      abaEmExibicao: t13,
      acompanhamentos,
      ...paginacao,
      documentosAbrindoDe,
      recarregar,
      carregarMais,
      verDocumento
    };
    $[36] = acompanhamentos;
    $[37] = carregarMais;
    $[38] = documentosAbrindoDe;
    $[39] = mostrarAba;
    $[40] = paginacao;
    $[41] = recarregar;
    $[42] = t13;
    $[43] = verDocumento;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  return t14;
}